export interface MapCoordinates {
  lat: number;
  lng: number;
}

const CITY_COORDINATES: Record<string, MapCoordinates> = {
  novaterra: { lat: 4.6984, lng: -74.2346 },
  mosquera: { lat: 4.6994, lng: -74.2344 },
  funza: { lat: 4.7164, lng: -74.2128 },
  madrid: { lat: 4.7325, lng: -74.2642 },
  chia: { lat: 4.8614, lng: -74.0583 },
  chía: { lat: 4.8614, lng: -74.0583 },
  cajica: { lat: 4.9192, lng: -74.0275 },
  cajicá: { lat: 4.9192, lng: -74.0275 },
  soacha: { lat: 4.5794, lng: -74.2169 },
  bogota: { lat: 4.711, lng: -74.0721 },
  'bogotá': { lat: 4.711, lng: -74.0721 },
  medellin: { lat: 6.2442, lng: -75.5812 },
  medellín: { lat: 6.2442, lng: -75.5812 },
  cali: { lat: 3.4516, lng: -76.532 },
  barranquilla: { lat: 10.9685, lng: -74.7813 },
  bucaramanga: { lat: 7.1254, lng: -73.1198 },
  cartagena: { lat: 10.391, lng: -75.4794 },
  pereira: { lat: 4.8133, lng: -75.6961 },
  manizales: { lat: 5.0689, lng: -75.5174 },
  'santa marta': { lat: 11.2408, lng: -74.199 },
  ibague: { lat: 4.4389, lng: -75.2322 },
  ibagué: { lat: 4.4389, lng: -75.2322 },
  villavicencio: { lat: 4.142, lng: -73.6266 },
};

export function resolveCoordinates(
  lat?: number,
  lng?: number,
  city?: string,
  neighborhood?: string
): MapCoordinates {
  if (typeof lat === 'number' && typeof lng === 'number' && !isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
    if (lat >= -4.5 && lat <= 13.5 && lng >= -82.0 && lng <= -66.0) {
      return { lat, lng };
    }
  }

  const lookupKey = (city || '').toLowerCase().trim();
  if (CITY_COORDINATES[lookupKey]) return CITY_COORDINATES[lookupKey];

  const neighKey = (neighborhood || '').toLowerCase().trim();
  for (const [k, coords] of Object.entries(CITY_COORDINATES)) {
    if (neighKey.includes(k) || lookupKey.includes(k)) return coords;
  }

  return { lat: 4.711, lng: -74.0721 };
}

export function calculateOsmTiles(lat: number, lon: number, zoom: number, width: number, height: number) {
  const scale = 256 * Math.pow(2, zoom);
  const x = ((lon + 180) / 360) * scale;
  const latRad = (lat * Math.PI) / 180;
  const y = ((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * scale;

  const originX = x - width / 2;
  const originY = y - height / 2;
  const startX = Math.floor(originX / 256);
  const endX = Math.floor((originX + width) / 256);
  const startY = Math.floor(originY / 256);
  const endY = Math.floor((originY + height) / 256);

  const tiles: Array<{ url: string; drawX: number; drawY: number }> = [];
  for (let tx = startX; tx <= endX; tx++) {
    for (let ty = startY; ty <= endY; ty++) {
      tiles.push({
        url: `/api/image-proxy?url=${encodeURIComponent(`https://tile.openstreetmap.org/${zoom}/${tx}/${ty}.png`)}`,
        drawX: Math.round(tx * 256 - originX),
        drawY: Math.round(ty * 256 - originY),
      });
    }
  }

  return { tiles, centerPixelX: width / 2, centerPixelY: height / 2 };
}

export function drawOsmPinAndLabels(
  ctx: CanvasRenderingContext2D,
  centerX: number,
  centerY: number,
  label: string,
  width: number,
  height: number
) {
  // Pin drop shadow
  ctx.beginPath();
  ctx.ellipse(centerX, centerY + 2, 7, 3.5, 0, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(10, 37, 64, 0.35)';
  ctx.fill();

  // Pin teardrop body
  ctx.beginPath();
  ctx.arc(centerX, centerY - 13, 9, Math.PI * 0.75, Math.PI * 2.25, false);
  ctx.lineTo(centerX, centerY);
  ctx.closePath();
  ctx.fillStyle = '#0a2540';
  ctx.fill();
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 1.5;
  ctx.stroke();

  // Pin center dot
  ctx.beginPath();
  ctx.arc(centerX, centerY - 13, 3.5, 0, Math.PI * 2);
  ctx.fillStyle = '#f59e0b';
  ctx.fill();

  // Top pill label
  ctx.font = 'bold 9px sans-serif';
  const textWidth = ctx.measureText(label).width;
  const boxW = Math.min(width - 20, textWidth + 12);
  const boxH = 16;
  const boxX = centerX - boxW / 2;
  const boxY = centerY - 32;

  ctx.fillStyle = '#0a2540';
  ctx.beginPath();
  ctx.roundRect(boxX, boxY, boxW, boxH, 4);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, centerX, boxY + boxH / 2);

  // Attribution
  ctx.font = '8px sans-serif';
  ctx.fillStyle = 'rgba(15, 23, 42, 0.75)';
  ctx.textAlign = 'right';
  ctx.fillText('© OpenStreetMap', width - 6, height - 5);
}
